/* Link between marketing pages. The href is always the crawlable URL ("/industries/construction/", "/ar/pricing/")
 * so crawlers, new tabs and copied links get the prerendered page. Inside the app document ("/") a plain click
 * stays a client-side route change; on a prerendered page it is a normal page load. */
import type { MouseEvent, ReactNode } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useI18n } from "../../../i18n";
import { isSeoRoute, onStaticPage, seoPath } from "../../../seo/paths";

type Props = { to: string; children: ReactNode; className?: string; style?: React.CSSProperties; "data-testid"?: string };

export function MktLink({ to, children, ...rest }: Props) {
  const { lang } = useI18n();
  const navigate = useNavigate();
  const href = seoPath(to, lang);
  const onClick = (e: MouseEvent<HTMLAnchorElement>) => {
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.altKey || e.ctrlKey || e.shiftKey || onStaticPage()) return;
    e.preventDefault();
    navigate(to);
  };
  return <a href={href} onClick={onClick} {...rest}>{children}</a>;
}

/** Language switch. On a marketing page it is a real link to the other language's URL ("/industries/garage/" ⇄
 * "/ar/industries/garage/"), so it works before the app has loaded and crawlers find both versions; inside the app
 * document a click switches in place. Elsewhere (sign-in, sign-up) it is a plain button. */
export function LangSwitch({ className, label, children }: { className: string; label: string; children: ReactNode }) {
  const i = useI18n();
  const { pathname } = useLocation();
  const other = i.isAr ? "en" : "ar";
  if (!isSeoRoute(pathname)) {
    return <button type="button" className={className} aria-label={label} title={label} onClick={i.toggleLang} data-testid="lang-toggle">{children}</button>;
  }
  const onClick = (e: MouseEvent<HTMLAnchorElement>) => {
    if (onStaticPage()) return; // the browser opens the other page, which loads in its language (and remembers it)
    e.preventDefault();
    i.toggleLang();
  };
  return <a className={className} href={seoPath(pathname, other)} hrefLang={other} aria-label={label} title={label} onClick={onClick} data-testid="lang-toggle">{children}</a>;
}
